import { TypedDocumentNode, gql } from '@apollo/client';
import { faAngleRight, faClock } from '@fortawesome/pro-solid-svg-icons';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  AUCTION_MARKET_URL,
  INSTANT_BUY_URL,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMainColor } from '@sorare/core/src/hooks/useMainColor';
import {
  getTextColorFromBackgroundColor,
  isLightColor,
} from '@sorare/core/src/lib/colors';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useHasInstantBuyCards } from 'hooks/useHasInstantBuyCards';

import { PromotionalEvent_PromotionalEvent } from './__generated__/index.graphql';

const Wrapper = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--unit);
  padding: var(--unit) var(--double-unit);

  flex: 1;

  & > * {
    z-index: 1;
    &:last-child {
      margin-left: auto;
    }
  }
  position: relative;
  &:before {
    --blur: 4px;
    content: '';
    inset: calc(-1 * var(--blur));
    position: absolute;
    border-radius: inherit;
    z-index: 0;
    background:
      linear-gradient(0deg, var(--c-nl-300), var(--c-nl-300)),
      var(--background) no-repeat center / cover;
    filter: blur(var(--blur));
    pointer-events: none;
    opacity: 1;
    transition: opacity ease-in 0.1s;
  }
  &:hover {
    &:before {
      background:
        linear-gradient(0deg, var(--c-nl-100), var(--c-nl-100)),
        var(--background) no-repeat center / cover;
    }
  }
  & > *:first-child {
    display: none;
    @media ${tabletAndAbove} {
      display: block;
    }
  }
`;

type Props = {
  promotionalEvent: PromotionalEvent_PromotionalEvent;
};

export const PromotionalEvent = ({ promotionalEvent }: Props) => {
  const { sport } = useSportContext();

  const { pictureUrl, slug, algoliaFilters } = promotionalEvent;
  const mainColor = useMainColor(pictureUrl ?? undefined);
  const instantBuyHasResults = useHasInstantBuyCards(sport, algoliaFilters);

  if (!sport) {
    return null;
  }

  const tab = instantBuyHasResults
    ? INSTANT_BUY_URL[sport]
    : AUCTION_MARKET_URL[sport];

  return (
    <Wrapper
      style={
        {
          '--background': `url(${pictureUrl})`,
          color: getTextColorFromBackgroundColor(mainColor),
        } as React.CSSProperties
      }
      to={generatePath(
        `${tab}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${slug}#market`
      )}
    >
      <Chip color={isLightColor(mainColor) ? 'black' : 'transparent'}>
        <Horizontal>
          <FontAwesomeIcon icon={faClock} />
          <TimeLeft time={promotionalEvent.endDate} />
        </Horizontal>
      </Chip>

      <BodyS
        style={{
          fontFamily: 'var(--rigid-square)',
          textTransform: 'uppercase',
          lineHeight: '14px',
        }}
        color={getTextColorFromBackgroundColor(mainColor)}
      >
        {promotionalEvent.title}
      </BodyS>

      <FontAwesomeIcon icon={faAngleRight} />
    </Wrapper>
  );
};

PromotionalEvent.fragments = {
  PromotionalEvent: gql`
    fragment PromotionalEvent_PromotionalEvent on PromotionalEventInterface {
      slug
      title
      pictureUrl
      endDate
      algoliaFilters
    }
  ` as TypedDocumentNode<PromotionalEvent_PromotionalEvent>,
};
